<template>
    <div>
        <h5 class="fw-bold mb-3">Dane do faktury i KSeF</h5>
        <p class="text-muted small mb-4">Wprowadź dane firmy do wystawiania faktur oraz skonfiguruj dostęp do Krajowego
            Systemu e-Faktur (KSeF).</p>

        <form @submit.prevent="handleSave">
            <div class="row g-3 max-width-wrapper">
                <!-- NIP Z PRZYCISKIEM GUS -->
                <div class="col-12">
                    <label class="form-label fw-semibold">Numer NIP</label>
                    <div class="input-group">
                        <input type="text" class="form-control" v-model="form.nip" maxlength="10"
                            placeholder="np. 1234567890" :disabled="saving" @input="handleNipInput" />
                        <button class="btn btn-outline-primary d-flex align-items-center gap-1" type="button"
                            :disabled="saving" @click="fetchGusData">
                            <i class="bi bi-search"></i>
                            <span>Pobierz z GUS</span>
                        </button>
                    </div>
                    <div class="form-text">Wpisz same cyfry (10 cyfr).</div>
                </div>

                <!-- NAZWA FIRMY -->
                <div class="col-12">
                    <label class="form-label fw-semibold">Nazwa firmy / Jan Kowalski</label>
                    <input type="text" class="form-control" v-model="form.companyName" :disabled="saving" />
                </div>

                <!-- ADRES -->
                <div class="col-12">
                    <label class="form-label fw-semibold">Ulica i numer</label>
                    <input type="text" class="form-control" v-model="form.address" :disabled="saving" />
                </div>

                <!-- KOD POCZTOWY I MIEJSCOWOŚĆ -->
                <div class="col-md-4">
                    <label class="form-label fw-semibold">Kod pocztowy</label>
                    <input type="text" class="form-control" v-model="form.postalCode" placeholder="00-000"
                        :disabled="saving" />
                </div>
                <div class="col-md-8">
                    <label class="form-label fw-semibold">Miejscowość</label>
                    <input type="text" class="form-control" v-model="form.city" :disabled="saving" />
                </div>

                <!-- NUMER BDO -->
                <div class="col-12">
                    <label class="form-label fw-semibold">Numer BDO</label>
                    <input type="text" class="form-control" v-model="form.bdo" placeholder="np. 000123456"
                        :disabled="saving" />
                    <div class="form-text">Opcjonalny numer w Rejestrze BDO.</div>
                </div>

                <!-- NAZWA BANKU -->
                <div class="col-12">
                    <label class="form-label fw-semibold">Nazwa banku</label>
                    <input type="text" class="form-control" v-model="form.bankName"
                        placeholder="np. PKO Bank Polski, mBank" :disabled="saving" />
                </div>

                <!-- NUMER RACHUNKU BANKOWEGO -->
                <div class="col-12">
                    <label class="form-label fw-semibold">Numer rachunku bankowego</label>
                    <input type="text" class="form-control font-monospace" v-model="form.bankAccount" maxlength="32"
                        placeholder="00 0000 0000 0000 0000 0000 0000" :disabled="saving"
                        @input="handleBankAccountInput" />
                    <div class="form-text">Numer konta jest automatycznie formatowany w grupy (26 cyfr).</div>
                </div>

                <!-- SEKCJA NUMERACJI DOKUMENTÓW -->
                <div class="col-12 mt-4 pt-3 border-top">
                    <h6 class="fw-bold mb-2">Konfiguracja numeracji dokumentów</h6>
                    <p class="text-muted small mb-3">Zdefiniuj sposób generowania kolejnych numerów faktur.</p>

                    <div class="row g-3">
                        <div class="col-md-6">
                            <label class="form-label fw-semibold">Cykl numeracji</label>
                            <select class="form-select" v-model="form.numberingPeriod" :disabled="saving">
                                <option value="monthly">Miesięczna (reset co miesiąc)</option>
                                <option value="yearly">Roczna (reset co rok)</option>
                            </select>
                        </div>

                        <div class="col-md-6">
                            <label class="form-label fw-semibold">Szablon numeracji</label>
                            <input type="text" class="form-control font-monospace" v-model="form.numberingPattern"
                                placeholder="" :disabled="saving" />
                        </div>
                        <button type="button" class="btn btn-sm btn-primary  align-items-center gap-1"
                            :disabled="saving" @click="applyDefaultPattern">
                            <i class="bi bi-magic"></i>
                            <span>Wybierz najczęstszy</span>
                        </button>
                        <div class="col-12">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                                <div class="form-text mb-0">
                                    Dostępne znaczniki: <code>{NR}</code> – numer, <code>{MM}</code> – miesiąc,
                                    <code>{YY}</code> – rok (2 cyfry), <code>{YYYY}</code> – rok (4 cyfry).
                                </div>

                            </div>

                            <div class="p-2 bg-light border rounded small">
                                <strong>Podgląd przykładowego numeru:</strong>
                                <span class="font-monospace text-primary ms-2 fw-bold">{{ previewDocNumber }}</span>
                            </div>

                        </div>
                    </div>
                </div>

                <!-- SEKCJA KSEF - KLUCZ -->
                <div class="col-12 mt-4 pt-3 border-top">
                    <h6 class="fw-bold mb-2">Integracja z KSeF</h6>
                    <p class="text-muted small mb-3">Załaduj plik klucza autoryzacyjnego / certyfikatu, aby umożliwić
                        automatyczne wysyłanie faktur do KSeF.</p>

                    <!-- INFORMACJA O STANIE CERTYFIKATU -->
                    <div v-if="form.hasKsefKey"
                        class="alert alert-success d-flex align-items-center gap-2 py-2 px-3 mb-3" role="alert">
                        <i class="bi bi-shield-check-fill fs-5"></i>
                        <div>
                            <strong>Certyfikat jest wgrany i aktywny.</strong>
                            <div class="small">Wgranie nowego pliku zastąpi dotychczasowy klucz na serwerze.</div>
                        </div>
                    </div>
                    <div v-else class="alert alert-secondary d-flex align-items-center gap-2 py-2 px-3 mb-3"
                        role="alert">
                        <i class="bi bi-shield-exclamation fs-5 text-muted"></i>
                        <div class="small">Brak certyfikatu KSeF na serwerze. Wybierz plik z dysku, aby go dodać.</div>
                    </div>

                    <label class="form-label fw-semibold">Plik klucza KSeF (.pem, .key, .crt, .p12)</label>
                    <input type="file" class="form-control" ref="keyFileRef" accept=".pem,.key,.crt,.p12"
                        :disabled="saving" @change="handleFileChange" />
                    <div class="form-text">Maksymalny rozmiar pliku: 40 KB.</div>
                </div>
            </div>

            <!-- PRZYCISK ZAPISU -->
            <div class="mt-4 max-width-wrapper">
                <button type="submit" class="btn btn-primary-dark d-flex align-items-center gap-2" :disabled="saving">
                    <i class="bi bi-floppy"></i>
                    <span>Zapisz zmiany</span>
                </button>
            </div>
        </form>

        <!-- LOADER -->
        <AppLoader :show="loading || saving" :text="loaderText" />
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import apiClient from '@/api'
import AppLoader from '@/components/AppLoader.vue'
import { webAlert } from '@/utils/dialog'

const loading = ref(false)
const saving = ref(false)
const loaderText = ref('')

const keyFileRef = ref(null)
const selectedFile = ref(null)

const form = ref({
    nip: '',
    companyName: '',
    address: '',
    postalCode: '',
    city: '',
    bdo: '',
    bankName: '',
    bankAccount: '',
    numberingPeriod: 'monthly',
    numberingPattern: '',
    hasKsefKey: false
})

// Ustawienie najczęstszego wzorca na podstawie wybranego okresu
const applyDefaultPattern = () => {
    if (form.value.numberingPeriod === 'monthly') {
        form.value.numberingPattern = '{NR}/{MM}/{YYYY}'
    } else {
        form.value.numberingPattern = '{NR}/{YYYY}'
    }
}

// Podgląd numeru w czasie rzeczywistym
const previewDocNumber = computed(() => {
    const pattern = form.value.numberingPattern || ''
    const now = new Date()
    const mm = String(now.getMonth() + 1).padStart(2, '0')
    const yy = String(now.getFullYear()).slice(-2)
    const yyyy = String(now.getFullYear())

    return pattern
        .replace('{NR}', '001')
        .replace('{MM}', mm)
        .replace('{YY}', yy)
        .replace('{YYYY}', yyyy)
})

const formatBankAccount = (value) => {
    const raw = value.replace(/\D/g, '').slice(0, 26)
    if (!raw) return ''

    const parts = []
    if (raw.length > 0) parts.push(raw.slice(0, 2))
    for (let i = 2; i < raw.length; i += 4) {
        parts.push(raw.slice(i, i + 4))
    }
    return parts.join(' ')
}

const handleNipInput = (e) => {
    form.value.nip = e.target.value.replace(/\D/g, '').slice(0, 10)
}

const handleBankAccountInput = (e) => {
    form.value.bankAccount = formatBankAccount(e.target.value)
}

const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
        if (file.size > 40 * 1024) {
            webAlert('Maksymalny rozmiar pliku klucza KSeF to 40 KB.')
            e.target.value = ''
            selectedFile.value = null
            return
        }
        selectedFile.value = file
    }
}

const fetchGusData = async () => {
    if (!form.value.nip) {
        webAlert('Wprowadź numer NIP przed pobraniem danych z GUS.')
        return
    }

    saving.value = true
    loaderText.value = 'Pobieranie danych z GUS...'

    try {
        const res = await apiClient.post({
            action: 'get_data_from_gus',
            nip: form.value.nip
        })

        if (res.data?.type && res.data?.data) {
            const company = res.data.data

            // Mapowanie zwróconych z GUS danych do formularza
            form.value.companyName = company.companyName || company.name || form.value.companyName
            form.value.address = company.address || company.street || form.value.address
            form.value.postalCode = company.zipcode || form.value.postalCode
            form.value.city = company.city || form.value.city
        } 

        webAlert(res.data)        
    } catch (error) {
        console.error('Błąd podczas pobierania z GUS:', error)
        webAlert('Wystąpił błąd podczas połączenia z serwerem GUS.')
    } finally {
        saving.value = false
    }
}

const loadData = async () => {
    loading.value = true
    loaderText.value = 'Pobieranie danych do faktury...'

    try {
        const response = await apiClient.post({ action: 'get-invoice-data' })

        if (response.data?.data) {
            const rawData = response.data.data
            const d = Array.isArray(rawData) ? rawData[0] : rawData

            if (d) {
                form.value.nip = d.nip || ''
                form.value.companyName = d.companyName || ''
                form.value.address = d.address || ''
                form.value.postalCode = d.postalCode || ''
                form.value.city = d.city || ''
                form.value.bdo = d.bdo || ''
                form.value.bankName = d.bankName || ''
                form.value.bankAccount = formatBankAccount(d.bankAccount || '')

                form.value.numberingPeriod = d.numberingPeriod || 'monthly'
                form.value.numberingPattern = d.numberingPattern || ''

                form.value.hasKsefKey = (d.hasKsefKey === 'yes' || d.hasKsefKey === true || d.hasKsefKey === 1 || d.hasKsefKey === '1')
            }
        }

    } catch (error) {
        webAlert('Błąd podczas pobierania danych do faktury.')
    } finally {
        loading.value = false
    }
}

const handleSave = async () => {
    saving.value = true
    loaderText.value = 'Zapisywanie danych do faktury...'

    try {
        const formDataPayload = new FormData()
        formDataPayload.append('action', 'save-invoice-data')
        formDataPayload.append('nip', form.value.nip)
        formDataPayload.append('companyName', form.value.companyName)
        formDataPayload.append('address', form.value.address)
        formDataPayload.append('postalCode', form.value.postalCode)
        formDataPayload.append('city', form.value.city)
        formDataPayload.append('bdo', form.value.bdo)
        formDataPayload.append('bankName', form.value.bankName)

        const cleanBankAccount = form.value.bankAccount.replace(/\s+/g, '')
        formDataPayload.append('bankAccount', cleanBankAccount)

        formDataPayload.append('numberingPeriod', form.value.numberingPeriod)
        formDataPayload.append('numberingPattern', form.value.numberingPattern)

        if (selectedFile.value) {
            formDataPayload.append('ksefKeyFile', selectedFile.value)
        }

        const response = await apiClient.post(formDataPayload)

        if (response.data?.hasKsefKey !== undefined) {
            form.value.hasKsefKey = (response.data.hasKsefKey === 'yes' || response.data.hasKsefKey === true || response.data.hasKsefKey === 1)
        } else if (selectedFile.value) {
            form.value.hasKsefKey = true
        }

        selectedFile.value = null
        if (keyFileRef.value) {
            keyFileRef.value.value = ''
        }

        saving.value = false; 
        webAlert(response.data)

    } catch (error) {
        saving.value = false;
        webAlert('Błąd podczas zapisywania danych.')
    } finally {
        saving.value = false
    }
}

onMounted(() => {
    loadData()
})
</script>

<style scoped>
.max-width-wrapper {
    max-width: 600px;
}

.font-monospace {
    font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}
</style>